import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronRight, CheckCircle2, ShoppingBag, Settings, CreditCard, Truck, Palette, Code, Package, Tags, BarChart3, Mail, Globe, Zap, Award } from 'lucide-react';

const ShopifyServices = () => {
  const storeSetup = [
    { area: 'Settings Configuration', details: 'Store address, regional currency, tax calculation, Refund/Privacy/Terms/Shipping policies' },
    { area: 'Domains & DNS', details: 'Custom domain connection (GoDaddy, Namecheap, Cloudflare) + SSL verification' },
    { area: 'Payment Processors', details: 'Shopify Payments, third-party gateways, Klarna, COD, UPI' },
    { area: 'Shipping & Delivery', details: 'Shipping profiles, flat/calculated carrier rates, local delivery, in-store pickup' },
  ];

  const frontendDev = [
    { area: 'Theme Architecture', details: 'OS 2.0 theme install & customization (Dawn, Horizon, and others)' },
    { area: 'Liquid Development', details: 'Custom sections, blocks, and snippets in Liquid, HTML, CSS, vanilla JS' },
    { area: 'Data Customization', details: 'Metafields & Metaobjects for custom product attributes, size charts, brand badges' },
    { area: 'Checkout Customization', details: 'Checkout branding/styling; Checkout UI Extensions & Shopify Functions (Plus)' },
  ];

  const catalogMerchandising = [
    { area: 'Product Ingestion', details: 'Titles, SEO descriptions, high-res media, variants, smart-tag automated collections' },
    { area: 'Inventory Control', details: 'Multi-location stock tracking, backorder rule configuration' },
    { area: 'Discounts & Bundles', details: 'Discount codes, automatic cart promotions, volume discounts, BOGO offers' },
  ];

  const appsIntegrations = [
    { area: 'App Ecosystem', details: 'Reviews (Judge.me, Loox), upsells, page builders (PageFly), loyalty programs' },
    { area: 'Marketing Pixels & Analytics', details: 'Web Pixels API setup — Meta Pixel, GA4, TikTok Pixel, Google Ads Enhanced Conversions' },
    { area: 'Sales Channels', details: 'Catalog sync with Meta Commerce, Google Merchant Center, Pinterest, TikTok Shop' },
    { area: 'Email Marketing', details: 'Klaviyo/Omnisend/Shopify Email integration, cart abandonment & post-purchase flows' },
  ];

  const whyChooseUs = [
    { icon: Zap, title: 'End-to-End Coverage', description: 'Settings to checkout to marketing pixels — one team handles everything.' },
    { icon: Award, title: 'Marketplace Expertise Applied', description: 'Same expertise powering your Amazon/Flipkart operations, applied to your own store.' },
    { icon: Settings, title: 'Built for Scale', description: 'Metafields, Functions, and app architecture that hold up as you grow.' },
    { icon: BarChart3, title: 'Full-Funnel Setup', description: 'Store, catalog, and tracking configured together, not in silos.' },
  ];

  const processSteps = [
    { step: '1', title: 'Share Requirements', description: 'Share your store requirements (new build or migration)' },
    { step: '2', title: 'Scope & Plan', description: 'We scope the setup, theme, and integration plan' },
    { step: '3', title: 'Go Live', description: 'Store goes live — configured, tracked, and ready to sell' },
  ];

  const TableSection = ({ title, icon: Icon, data, iconColor }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="mb-12"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 ${iconColor} rounded-lg flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <h3 className="font-heading text-xl font-bold">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[500px] border-collapse bg-white rounded-xl shadow-sm">
          <thead>
            <tr className="bg-green-50">
              <th className="text-left py-3 px-5 font-heading font-bold text-slate-800 border-b-2 border-green-100 w-1/3">Area</th>
              <th className="text-left py-3 px-5 font-heading font-bold text-slate-800 border-b-2 border-green-100">What We Handle</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => (
              <tr key={idx} className="border-b border-slate-100 hover:bg-green-50/50 transition-colors">
                <td className="py-3 px-5">
                  <span className="font-semibold text-slate-800">{item.area}</span>
                </td>
                <td className="py-3 px-5 text-slate-600">{item.details}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Shopify Store Development Services | Obiquiste Solutions</title>
        <meta name="description" content="Professional Shopify store development from setup to scale. Theme customization, catalog management, app integrations, marketing pixels & multi-channel sync. Built to sell, not just launch." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/shopify-store-development" />
      </Helmet>
      
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-green-600 font-medium">Shopify Development</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <ShoppingBag className="w-4 h-4 mr-2" />
                E-commerce Development
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Shopify Store <span className="text-green-600">Development</span>
              </h1>
              <p className="text-xl md:text-2xl text-slate-700 mb-4 font-medium">
                From setup to scale — a Shopify store built to sell, not just launch.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Whether you're starting fresh or migrating an existing store, we handle every layer of Shopify — configuration, design, catalog, and the app ecosystem that turns traffic into revenue.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?service=shopify">
                  <Button className="bg-green-600 text-white hover:bg-green-700 px-8 py-6 rounded-full text-base font-semibold">
                    Request Free Consultation
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Sections */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Complete Shopify Services
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              We cover every aspect of your Shopify store — from initial setup to advanced customizations and integrations.
            </p>
          </motion.div>

          <TableSection 
            title="Store Onboarding & Setup" 
            icon={Settings} 
            data={storeSetup}
            iconColor="bg-green-500"
          />

          <TableSection 
            title="Front-End & Theme Development" 
            icon={Palette} 
            data={frontendDev}
            iconColor="bg-emerald-500"
          />

          <TableSection 
            title="Catalog & Merchandising" 
            icon={Package} 
            data={catalogMerchandising}
            iconColor="bg-teal-500"
          />

          <TableSection 
            title="Apps, Integrations & Tracking" 
            icon={BarChart3} 
            data={appsIntegrations}
            iconColor="bg-green-600"
          />
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Why Obiquiste for Your Shopify Store
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20 text-center"
              >
                <div className="w-14 h-14 bg-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-green-400" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-300 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Started Process */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Get Started
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Three simple steps to launch your Shopify store
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {processSteps.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-md border border-slate-200 text-center relative"
              >
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold text-xl">
                  {item.step}
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
                {idx < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-8 h-8 text-green-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-green-600 to-emerald-600">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Launch Your Shopify Store?
          </h2>
          <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">
            Get a Shopify store that's configured, tracked, and ready to sell from day one.
          </p>
          <Link to="/contact?service=shopify">
            <Button className="bg-white text-green-600 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold">
              Request Free Consultation
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ShopifyServices;
